import { observer } from 'mobx-react-lite';
import type { ReactNode } from 'react';
import { Alert, Button, Spinner } from 'react-bootstrap';
import { ArrowRepeat } from 'react-bootstrap-icons';

import { DetailsPane } from './DetailsPane';
import type { RecordDetailsModel } from './RecordDetailsModel';

export interface RecordDetailsPaneProps<T> {
  readonly model: RecordDetailsModel<T>;
  readonly icon: string;
  /** The object type, e.g. "Trigger". */
  readonly kind: string;
  readonly onClose: () => void;
  readonly name: (record: T) => string;
  readonly actions: (record: T) => ReactNode;
  readonly children: (record: T) => ReactNode;
}

/** Re-reads the record; spins while a read is in flight. */
const RefreshButton = ({ busy, onClick }: { readonly busy: boolean; readonly onClick: () => void }) => (
  <Button variant="phoenix-secondary" size="sm" onClick={onClick} disabled={busy} title="Refresh" aria-label="Refresh">
    <ArrowRepeat className={busy ? 'details-spin' : undefined} />
  </Button>
);

/**
 * A details pane for one record: its header and content once loaded, and what is going
 * on otherwise. The header (and so the close button) is there in every state. While a
 * re-read is in flight the previous record stays, dimmed.
 *
 * A failed read shows the error, not the record from before it: the record's actions
 * would act on something this pane can no longer vouch for. Retry and the header's
 * refresh button read it again.
 */
const RecordDetailsPaneImpl = <T,>({ model, icon, kind, onClose, name, actions, children }: RecordDetailsPaneProps<T>) => {
  const state = model.state;
  const noun = kind.toLowerCase();
  const refresh = () => model.refresh();
  if (state.kind !== 'loaded') {
    // No record, so no record actions, also while a re-read is in flight.
    const busy = (state.kind === 'not-found' && state.refreshing) || (state.kind === 'failed' && state.retrying);
    return (
      <DetailsPane icon={icon} kind={kind} actions={state.kind !== 'loading' && <RefreshButton busy={busy} onClick={refresh} />} onClose={onClose}>
        {state.kind === 'loading' && (
          <div className="p-4 text-center">
            <Spinner animation="border" size="sm" />
          </div>
        )}
        {state.kind === 'not-found' && <div className="p-4 text-muted">This {noun} no longer exists.</div>}
        {state.kind === 'failed' && (
          <Alert variant="danger" className="m-3 d-flex align-items-center gap-3">
            <span className="flex-grow-1">
              The {noun} could not be loaded: {state.error.message}
            </span>
            <Button variant="outline-danger" size="sm" onClick={refresh} disabled={state.retrying}>
              {state.retrying ? <Spinner animation="border" size="sm" /> : 'Retry'}
            </Button>
          </Alert>
        )}
      </DetailsPane>
    );
  }
  return (
    <DetailsPane
      icon={icon}
      kind={kind}
      name={name(state.record)}
      actions={
        <div className="d-flex gap-1">
          <RefreshButton busy={state.refreshing} onClick={refresh} />
          {actions(state.record)}
        </div>
      }
      onClose={onClose}
    >
      <div style={state.refreshing ? { opacity: 0.6 } : undefined}>
        {state.incomplete && (
          <Alert variant="warning" className="m-3 mb-0">
            Part of this {noun} could not be read, so some values may be missing or wrong.
          </Alert>
        )}
        {children(state.record)}
      </div>
    </DetailsPane>
  );
};

export const RecordDetailsPane = observer(RecordDetailsPaneImpl) as typeof RecordDetailsPaneImpl;
